import type { PipeTransform } from '@angular/core';
import { inject, InjectionToken, Pipe } from '@angular/core';

import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { extractNameFromURL } from '@recruitee/utils';

const socialMediaMapping = [
  { url: 'about.me', type: 'aboutme', name: 'About.me' },
  { url: 'academia.edu', type: 'academiaedu', name: 'Academia.edu' },
  { url: 'thisisamplify.com', type: 'amplify' },
  { url: 'angel.co', type: 'angellist', name: 'Wellfound' },
  { url: 'bandcamp.com', type: 'bandcamp' },
  { url: 'bebo.com', type: 'bebo', name: 'Poply' },
  { url: 'behance.net', type: 'behance' },
  { url: 'bitbucket.org', type: 'bitbucket' },
  { url: 'blip.fm', type: 'blipfm' },
  { url: 'blogger.com', type: 'blogger' },
  { url: 'crunchbase.com', type: 'crunchbase' },
  { url: 'dandyid.org', type: 'dandyid' },
  { url: 'delicious.com', type: 'delicious' },
  { url: 'deviantart.com', type: 'deviantart', name: 'DeviantArt' },
  { url: 'digg.com', type: 'digg' },
  { url: 'diigo.com', type: 'diigo' },
  { url: 'disqus.com', type: 'disqus' },
  { url: 'doyoubuzz.com', type: 'doyoubuzz', name: 'DoYouBuzz' },
  { url: 'dribbble.com', type: 'dribbble' },
  { url: 'econsultancy.com', type: 'econsultancy' },
  { url: 'flavors.me', type: 'flavorsme' },
  { url: 'flickr.com', type: 'flickr' },
  { url: 'foursquare.com', type: 'foursquare' },
  { url: 'friendster.com', type: 'friendster' },
  { url: 'getsatisfaction.com', type: 'getsatisfaction' },
  { url: 'github.com', type: 'github', name: 'GitHub' },
  { url: 'goodreads.com', type: 'goodreads' },
  { url: 'plus.google.com', type: 'googleplus' },
  { url: 'gravatar.com', type: 'gravatar' },
  { url: 'news.ycombinator.com', type: 'hackernews', name: 'Hacker News' },
  { url: 'hi5.com', type: 'hi5' },
  { url: 'hypem.com', type: 'hypemachine', name: 'Hype Machine' },
  { url: 'imdb.com', type: 'imdb', name: 'IMDb' },
  { url: 'intensedebate.com', type: 'intensedebate', name: 'IntenseDebate' },
  { url: 'instagram.com', type: 'instagram' },
  { url: 'klout.com', type: 'klout', name: 'Khoros' },
  { url: 'lanyrd.com', type: 'lanyrd', name: 'Eventbrite' },
  { url: 'last.fm', type: 'lastfm', name: 'Last.fm' },
  { url: 'linkedin.com', type: 'linkedin', name: 'LinkedIn' },
  { url: 'meetup.com', type: 'meetup' },
  { url: 'mixcloud.com', type: 'mixcloud' },
  { url: 'pandora.com', type: 'pandora' },
  { url: 'picasa.google.com', type: 'picasa' },
  { url: 'pinboard.in', type: 'pinboard' },
  { url: 'pinterest.com', type: 'pinterest' },
  { url: 'plancast.com', type: 'plancast' },
  { url: 'plaxo.com', type: 'plaxo' },
  { url: 'plurk.com', type: 'plurk' },
  { url: 'quora.com', type: 'quora' },
  { url: 'reddit.com', type: 'reddit' },
  { url: 'reverbnation.com', type: 'reverbnation', name: 'ReverbNation' },
  { url: 'scribd.com', type: 'scribd' },
  { url: 'shelfari.com', type: 'shelfari', name: 'Goodreads' },
  { url: 'skype.com', type: 'skype' },
  { url: 'slideshare.net', type: 'slideshare', name: 'SlideShare' },
  { url: 'smugmug.com', type: 'smugmug', name: 'SmugMug' },
  { url: 'soundcloud.com', type: 'soundcloud', name: 'SoundCloud' },
  { url: 'stackexchange.com', type: 'stackexchange', name: 'Stack Exchange' },
  { url: 'stackoverflow.com', type: 'stackoverflow', name: 'Stack Overflow' },
  { url: 'stumbleupon.com', type: 'stumbleupon', name: 'StumbleUpon' },
  { url: 'tagged.com', type: 'tagged' },
  { url: 'tiktok.com', type: 'tiktok' },
  { url: 'tripit.com', type: 'tripit', name: 'TripIt' },
  { url: 'tumblr.com', type: 'tumblr' },
  { url: 'twitter.com', type: 'twitter' },
  { url: 'typepad.com', type: 'typepad' },
  { url: 'vimeo.com', type: 'vimeo' },
  { url: 'vk.com', type: 'vk', name: 'VK' },
  { url: 'wordpress.com', type: 'wordpress', name: 'WordPress' },
  { url: 'x.com', type: 'x' },
  { url: 'xing.com', type: 'xing' },
  { url: 'yelp.com', type: 'yelp' },
  { url: 'youtube.com', type: 'youtube', name: 'YouTube' },
  { url: 'facebook.com', type: 'facebook' },
  { url: 'fb.com', type: 'facebook', name: 'Facebook' },
];

export const IconProvider = new InjectionToken<(name: string) => string>('IconProvider');

@Pipe({
  name: 'rtSocialMedia',
  standalone: true,
})
export class SocialMediaPipe implements PipeTransform {
  private url$ = new BehaviorSubject<{ url: string; withName: boolean }>({
    url: '',
    withName: false,
  });
  private iconWithOptionalName$ = this.url$.pipe(
    distinctUntilChanged((a, b) => a.url === b.url && a.withName === b.withName),
    map(({ url, withName }) => {
      const typeItem = this.getTypeItem(url);

      if (!withName || !typeItem) {
        return {
          type: typeItem ? typeItem.type : 'unknown',
          name: withName ? this.resolveName({ url }) : undefined,
        };
      }

      return {
        type: typeItem.type,
        name: this.resolveName(typeItem),
      };
    }),
    switchMap(({ type, name }) => of({ icon: this.iconProvider(type), name })),
    shareReplay(),
  );

  private iconProvider = inject(IconProvider);

  public transform(
    url: string,
    withName: boolean = false,
  ): Observable<{ icon: string; name?: string }> {
    this.url$.next({ url, withName });
    return this.iconWithOptionalName$;
  }

  private getTypeItem(url: string) {
    if (!url) return false;
    return socialMediaMapping.find(sm => url.indexOf(sm.url) !== -1);
  }

  private resolveName(item: { url: string; name?: string }): string {
    if (item.name?.length) {
      return item.name;
    }

    return extractNameFromURL(item.url);
  }
}
